import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Avatar,
  Tooltip,
  Grid,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import {
  withdrawalService,
  Withdrawal,
  WithdrawalStatus,
  PayoutMethod,
  WithdrawalFilters,
} from '../services/withdrawalService';

const Withdrawals: React.FC = () => {
  // State management
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WithdrawalStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'createdAt' | 'amount' | 'status'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Dialog state
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [rejectRemarks, setRejectRemarks] = useState('');

  // Fetch withdrawals
  const fetchWithdrawals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: WithdrawalFilters = {
        page,
        limit,
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sortBy,
        sortOrder,
      };

      const response = await withdrawalService.getWithdrawals(filters);
      
      if (response.success) {
        setWithdrawals(response.data.withdrawals);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.totalItems);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  // Handle view details
  const handleViewDetails = async (withdrawal: Withdrawal) => {
    try {
      setLoading(true);
      const response = await withdrawalService.getWithdrawal(withdrawal.id);
      if (response.success) {
        setSelectedWithdrawal(response.data.withdrawal);
        setDetailDialogOpen(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch withdrawal details');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve
  const handleApprove = async () => {
    if (!selectedWithdrawal) return;
    
    try {
      setActionLoading(true);
      setError(null);
      await withdrawalService.approveWithdrawal(selectedWithdrawal.id, {
        referenceId: referenceId || undefined,
      });
      setSuccessMessage('Withdrawal approved successfully');
      setApproveDialogOpen(false);
      setReferenceId('');
      setDetailDialogOpen(false);
      fetchWithdrawals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!selectedWithdrawal) return;
    
    try {
      setActionLoading(true);
      setError(null);
      await withdrawalService.rejectWithdrawal(selectedWithdrawal.id, {
        remarks: rejectRemarks || undefined,
      });
      setSuccessMessage('Withdrawal rejected successfully');
      setRejectDialogOpen(false);
      setRejectRemarks('');
      setDetailDialogOpen(false);
      fetchWithdrawals();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Get status color
  const getStatusColor = (status: WithdrawalStatus) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return 'warning';
      case WithdrawalStatus.APPROVED:
        return 'success';
      case WithdrawalStatus.REJECTED:
        return 'error';
      case WithdrawalStatus.PAID:
        return 'info';
      default:
        return 'default';
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Get stats
  const stats = {
    total: totalItems,
    pending: withdrawals.filter(w => w.status === WithdrawalStatus.PENDING).length,
    approved: withdrawals.filter(w => w.status === WithdrawalStatus.APPROVED).length,
    rejected: withdrawals.filter(w => w.status === WithdrawalStatus.REJECTED).length,
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Withdrawal Management
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Withdrawals
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Approved
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.approved}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Rejected
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.rejected}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search by UPI, account number, name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value={WithdrawalStatus.PENDING}>Pending</MenuItem>
                  <MenuItem value={WithdrawalStatus.APPROVED}>Approved</MenuItem>
                  <MenuItem value={WithdrawalStatus.REJECTED}>Rejected</MenuItem>
                  <MenuItem value={WithdrawalStatus.PAID}>Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <MenuItem value="createdAt">Date</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => setSortOrder(e.target.value as any)}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Withdrawals Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Doctor</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payout Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : withdrawals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography>No withdrawals found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                withdrawals.map((withdrawal) => (
                  <TableRow key={withdrawal.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        #{withdrawal.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={withdrawal.doctor?.profileImage}
                          sx={{ width: 32, height: 32 }}
                        >
                          {withdrawal.doctor?.fullName?.[0] || 'D'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {withdrawal.doctor?.fullName || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {withdrawal.doctor?.email || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(Number(withdrawal.amount), withdrawal.currency)}
                      </Typography>
                      {withdrawal.fee > 0 && (
                        <Typography variant="caption" color="textSecondary">
                          Fee: {formatCurrency(Number(withdrawal.fee), withdrawal.currency)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={withdrawal.payoutMethod.toUpperCase()}
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                        color={getStatusColor(withdrawal.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(withdrawal.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(withdrawal.createdAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(withdrawal)}
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {withdrawal.status === WithdrawalStatus.PENDING && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal);
                                  setApproveDialogOpen(true);
                                }}
                                color="success"
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal);
                                  setRejectDialogOpen(true);
                                }}
                                color="error"
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Withdrawal Details</DialogTitle>
        <DialogContent>
          {selectedWithdrawal && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom>Doctor Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Doctor Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedWithdrawal.doctor?.fullName || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{selectedWithdrawal.doctor?.email || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{selectedWithdrawal.doctor?.phoneNumber || '-'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Withdrawal Information</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Amount</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(Number(selectedWithdrawal.amount), selectedWithdrawal.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Fee</Typography>
                  <Typography variant="body1">
                    {formatCurrency(Number(selectedWithdrawal.fee), selectedWithdrawal.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Net Amount</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatCurrency(Number(selectedWithdrawal.netAmount), selectedWithdrawal.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip
                    label={selectedWithdrawal.status.charAt(0).toUpperCase() + selectedWithdrawal.status.slice(1)}
                    color={getStatusColor(selectedWithdrawal.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Payout Method</Typography>
                  <Typography variant="body1">{selectedWithdrawal.payoutMethod.toUpperCase()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">Requested Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedWithdrawal.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
                {selectedWithdrawal.processedAt && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">Processed Date</Typography>
                    <Typography variant="body1">
                      {new Date(selectedWithdrawal.processedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                )}

                {selectedWithdrawal.payoutMethod === PayoutMethod.UPI && selectedWithdrawal.upiId && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>UPI Details</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">UPI ID</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedWithdrawal.upiId}
                      </Typography>
                    </Grid>
                  </>
                )}

                {selectedWithdrawal.payoutMethod === PayoutMethod.BANK && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>Bank Details</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Account Holder Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedWithdrawal.accountHolderName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Account Number</Typography>
                      <Typography variant="body1">{selectedWithdrawal.accountNumber || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">IFSC Code</Typography>
                      <Typography variant="body1">{selectedWithdrawal.ifscCode || '-'}</Typography>
                    </Grid>
                  </>
                )}

                {selectedWithdrawal.referenceId && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Reference ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedWithdrawal.referenceId}
                    </Typography>
                  </Grid>
                )}

                {selectedWithdrawal.remarks && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Remarks</Typography>
                    <Typography variant="body1">{selectedWithdrawal.remarks}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedWithdrawal?.status === WithdrawalStatus.PENDING && (
            <>
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  setApproveDialogOpen(true);
                }}
                color="success"
                variant="contained"
                startIcon={<CheckCircleIcon />}
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  setRejectDialogOpen(true);
                }}
                color="error"
                variant="outlined"
                startIcon={<CancelIcon />}
              >
                Reject
              </Button>
            </>
          )}
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}  fullWidth>
        <DialogTitle>Approve Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedWithdrawal && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(Number(selectedWithdrawal.amount), selectedWithdrawal.currency)}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Reference ID (Optional)"
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              placeholder="Enter transaction reference ID"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            color="success"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {actionLoading ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} fullWidth>
        <DialogTitle>Reject Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedWithdrawal && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">Amount</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(Number(selectedWithdrawal.amount), selectedWithdrawal.currency)}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="Rejection Reason (Optional)"
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              placeholder="Enter reason for rejection"
              multiline
              rows={4}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {actionLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Withdrawals;

